import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarDays,
  Check,
  ClipboardCheck,
  House,
  ListChecks,
  LockKeyhole,
  MapPinned,
  Settings,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import type { EventReadiness } from '@wht-xc-utils/shared/domain';

import { api } from '../lib/api';

interface EventResponse {
  event: { id: string; name: string };
}

export function EventTabs() {
  const { eventId } = useParams<{ eventId: string }>();
  const location = useLocation();
  const { data } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => api.get<EventResponse>(`/api/events/${eventId}`),
    enabled: !!eventId,
  });
  const readiness = useQuery({
    queryKey: ['eventReadiness', eventId],
    queryFn: () => api.get<{ readiness: EventReadiness }>(`/api/events/${eventId}/readiness`),
    enabled: !!eventId,
  });
  const state = readiness.data?.readiness;
  const tabs: Array<{
    to: string;
    label: string;
    detail: string;
    enabled: boolean;
    complete: boolean;
    reason: string;
    icon: LucideIcon;
  }> = [
    {
      to: `/events/${eventId}`,
      label: 'Home',
      detail: 'Overview',
      enabled: true,
      complete: true,
      reason: '',
      icon: House,
    },
    {
      to: `/events/${eventId}/entries`,
      label: 'Entries',
      detail: 'Riders',
      enabled: true,
      complete: state?.entriesReady ?? false,
      reason: '',
      icon: ListChecks,
    },
    {
      to: `/events/${eventId}/schedule`,
      label: 'Schedule',
      detail: 'Days & order',
      enabled: state?.scheduleEnabled ?? false,
      complete: state?.scheduleReady ?? false,
      reason: 'Import entries first.',
      icon: CalendarDays,
    },
    {
      to: `/events/${eventId}/volunteers`,
      label: 'Volunteers',
      detail: 'Shifts',
      enabled: state?.volunteersEnabled ?? false,
      complete: state?.volunteersReady ?? false,
      reason: 'Review the schedule first.',
      icon: UsersRound,
    },
    {
      to: `/events/${eventId}/jumps`,
      label: 'Jumps',
      detail: 'Courses & posts',
      enabled: state?.jumpsEnabled ?? false,
      complete: state?.jumpsReady ?? false,
      reason: 'Review the schedule first.',
      icon: MapPinned,
    },
    {
      to: `/events/${eventId}/assignments`,
      label: 'Assignments',
      detail: 'Coverage',
      enabled: state?.assignmentsEnabled ?? false,
      complete: false,
      reason: assignmentBlockedReason(state),
      icon: ClipboardCheck,
    },
  ];
  return (
    <section className="event-context">
      <div className="event-context-heading">
        <div>
          <span className="eyebrow">Event workspace</span>
          <h1>{data?.event.name ?? 'Event'}</h1>
        </div>
        <Link
          className={`event-admin-link${location.pathname.startsWith(`/events/${eventId}/admin`) ? ' active' : ''}`}
          to={`/events/${eventId}/admin`}
        >
          <Settings aria-hidden="true" />
          Administration
        </Link>
      </div>
      <nav className="event-tabs" aria-label="Event setup">
        {tabs.map((t) => {
          const active = location.pathname === t.to;
          const status = !t.enabled
            ? 'locked'
            : active
              ? 'active'
              : t.complete
                ? 'complete'
                : 'available';
          const Icon = t.icon;
          const content = (
            <>
              <span className="workflow-step-icon" aria-hidden="true">
                {status === 'complete' ? (
                  <Check />
                ) : status === 'locked' ? (
                  <LockKeyhole />
                ) : (
                  <Icon />
                )}
              </span>
              <span className="workflow-step-copy">
                <strong>{t.label}</strong>
                <small>{t.detail}</small>
              </span>
            </>
          );
          return t.enabled ? (
            <Link key={t.to} to={t.to} className={`workflow-step ${status}`}>
              {content}
            </Link>
          ) : (
            <span key={t.to} className="workflow-step locked" aria-disabled="true" title={t.reason}>
              {content}
            </span>
          );
        })}
      </nav>
    </section>
  );
}

function assignmentBlockedReason(readiness: EventReadiness | undefined): string {
  if (!readiness?.scheduleReady) return 'Complete the schedule first.';
  if (!readiness.volunteersReady) return 'Import XC jump-judge shifts first.';
  if (!readiness.jumpsReady) return 'Import jumps first.';
  return '';
}
